import { useContext } from "react";
import { AuthContext, type AuthContextValue } from "@/contexts/auth-context";
import { permKeysWithAliases } from "@/domain/menus";

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function usePerm(key: string): boolean {
  return useAuth().hasPerm(key);
}

/** 옛 별칭 키까지 포함해 판정 (예: material.price ← material.view). hasPerm 자체 동작은 그대로. */
export function hasPermWithAliases(hasPerm: (key: string) => boolean, key: string): boolean {
  return permKeysWithAliases(key).some((k) => hasPerm(k));
}

/**
 * 원자재 단가·금액 보기(material.price, 옛 material.view 포함).
 * ⚠️ 화면 표시만 가린다. 입고 문서에 단가가 같이 있어 브라우저에는 그대로 내려온다 —
 * 실제 데이터 차단은 단가 분리(wiki/시스템/부서별-권한.md §5-C) 때 단가 전용 문서 + rules 로 한다.
 */
export function useMaterialPricePerm(): boolean {
  const { hasPerm } = useAuth();
  return hasPermWithAliases(hasPerm, "material.price");
}

/**
 * 4자리 배합 코드(bcode·code) 보기(shipment.mix). 규격 문자열(25-24-150)은 이 권한과 무관하게 보인다.
 * ⚠️ 화면 표시만 가린다 — 출하 문서에 코드가 같이 있다. 기사용 사본 분리는 §5-D.
 */
export function useShipmentMixPerm(): boolean {
  return useAuth().hasPerm("shipment.mix");
}
